import type { ReactNode } from 'react'

import { useEffect, useId, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { X } from 'lucide-react'
import { cn } from '@/lib/utils'

interface SheetProps {
    open: boolean
    onClose: () => void
    title: ReactNode
    eyebrow?: ReactNode
    children: ReactNode
    footer?: ReactNode
    className?: string
}

/** A slide-over panel: from the right on wide screens, from the bottom on phones. */
export const Sheet = ({
    open,
    onClose,
    title,
    eyebrow,
    children,
    footer,
    className
}: SheetProps): ReactNode => {
    const titleId = useId()
    const panelRef = useRef<HTMLDivElement | null>(null)
    const closeRef = useRef<HTMLButtonElement | null>(null)
    const returnFocus = useRef<HTMLElement | null>(null)
    const [mounted, setMounted] = useState(false)
    const closeHandler = useRef(onClose)

    closeHandler.current = onClose

    useEffect(() => {
        setMounted(true)
    }, [])

    useEffect(() => {
        if (!open) return

        const onClose = () => closeHandler.current()

        returnFocus.current = document.activeElement as HTMLElement | null

        const previous = document.body.style.overflow

        document.body.style.overflow = 'hidden'
        window.setTimeout(() => closeRef.current?.focus(), 60)

        const handleKey = (event: KeyboardEvent) => {
            if (event.key === 'Escape') {
                event.stopPropagation()
                onClose()
                return
            }

            if (event.key !== 'Tab' || !panelRef.current) return

            const focusable = Array.from(panelRef.current.querySelectorAll<HTMLElement>('a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'))
            const first = focusable[0]
            const last = focusable[focusable.length - 1]

            if (event.shiftKey && document.activeElement === first) {
                event.preventDefault()
                last?.focus()
            }
            else if (!event.shiftKey && document.activeElement === last) {
                event.preventDefault()
                first?.focus()
            }
        }

        document.addEventListener('keydown', handleKey)

        return () => {
            document.body.style.overflow = previous
            document.removeEventListener('keydown', handleKey)
            returnFocus.current?.focus?.()
        }
    }, [open])

    if (!mounted) return null

    return createPortal(
        <div
            className={cn('fixed inset-0 z-[75] transition-[visibility] duration-700', open ? 'visible' : 'invisible')}
            aria-hidden={!open}
        >
            <div
                className={cn('absolute inset-0 bg-black/70 backdrop-blur-[2px] transition-opacity duration-700', open ? 'opacity-100' : 'opacity-0')}
                onClick={onClose}
            />

            <div
                ref={panelRef}
                role='dialog'
                aria-modal='true'
                aria-labelledby={titleId}
                className={cn(
                    'absolute flex flex-col border-hair bg-ink-2 transition-transform duration-700 ease-[cubic-bezier(0.22,0.61,0.36,1)]',
                    'inset-x-0 bottom-0 max-h-[88svh] rounded-t-2xl border-t',
                    'md:inset-y-0 md:left-auto md:right-0 md:max-h-none md:w-[30rem] md:rounded-none md:border-l md:border-t-0',
                    open ? 'translate-x-0 translate-y-0' : 'translate-y-full md:translate-x-full md:translate-y-0',
                    className
                )}
            >
                <div className='flex items-start justify-between gap-6 border-b border-hair px-6 pb-5 pt-6 md:px-8 md:pt-8'>
                    <div>
                        {eyebrow && (
                            <p className='label-mono text-brass'>
                                {eyebrow}
                            </p>
                        )}
                        <h2 id={titleId} className='mt-2 font-heading text-[2.1rem] font-light leading-tight text-star'>
                            {title}
                        </h2>
                    </div>
                    <button
                        ref={closeRef}
                        type='button'
                        onClick={onClose}
                        aria-label='Close'
                        className='mt-1 inline-flex size-10 shrink-0 items-center justify-center rounded-full border border-hair-2 text-star-2 transition-colors hover:border-brass hover:text-star'
                    >
                        <X aria-hidden className='size-4' strokeWidth={1.25} />
                    </button>
                </div>

                <div className='flex-1 overflow-y-auto px-6 py-6 md:px-8'>
                    {children}
                </div>

                {footer && (
                    <div className='border-t border-hair px-6 py-5 md:px-8'>
                        {footer}
                    </div>
                )}
            </div>
        </div>,
        document.body
    )
}
